import { shallowRef } from "vue"

import { deleteJson, patchJson, postJson } from "@/lib/fetch"
import {
  analyzeBatchGroupPath,
  archiveBatchPath,
  attachBatchGroupPath,
  batchContainerPath,
  batchPath,
  batchGroupItemPath,
  captureGroupPhotoPath,
  finishBatchPath,
  mergeBatchGroupPath,
  rotateBatchGroupPath,
  skipBatchGroupPath,
  splitBatchGroupPath,
} from "@/routes"
import type { CaptureBatch, CaptureGroup } from "@/types/capture"

export interface BatchItemFields {
  name?: string | null
  description?: string | null
  condition?: string | null
  quantity?: number | null
  model_number?: string | null
  serial_number?: string | null
  item_type_id?: string | null
  parent_id?: string | null
  is_container?: boolean
}

type BatchMutationAction =
  | "analyze"
  | "create"
  | "attach"
  | "skip"
  | "delete-photo"
  | "split"
  | "merge"
  | "rotate"
  | "finish"
  | "archive"
  | "container"
  | "delete"

interface UseBatchMutationsOptions {
  replaceGroup: (group: CaptureGroup) => void
  replaceBatch: (batch: CaptureBatch) => void
  replaceGroups?: (groups: CaptureGroup[]) => void
}

interface DataEnvelope<T> {
  data: T
}

interface ErrorEnvelope {
  error?: { message?: string }
}

export function useBatchMutations(options: UseBatchMutationsOptions) {
  const pendingActions = shallowRef<Record<string, BatchMutationAction>>({})
  const errors = shallowRef<Record<string, string>>({})

  function errorFor(id: string): string | null {
    return errors.value[id] ?? null
  }

  function actionFor(id: string): BatchMutationAction | null {
    return pendingActions.value[id] ?? null
  }

  async function groupMutation(
    groupId: string,
    action: BatchMutationAction,
    request: () => Promise<Response>,
  ): Promise<CaptureGroup | null> {
    return mutation(groupId, action, request, options.replaceGroup)
  }

  async function mutation<T>(
    id: string,
    action: BatchMutationAction,
    request: () => Promise<Response>,
    replace: (record: T) => void,
  ): Promise<T | null> {
    if (actionFor(id)) return null

    pendingActions.value = { ...pendingActions.value, [id]: action }
    clearError(id)
    try {
      const record = await responseData<T>(await request())
      replace(record)
      return record
    } catch (error) {
      errors.value = {
        ...errors.value,
        [id]: error instanceof Error ? error.message : "The request failed",
      }
      return null
    } finally {
      const next = { ...pendingActions.value }
      delete next[id]
      pendingActions.value = next
    }
  }

  function clearError(id: string) {
    const next = { ...errors.value }
    delete next[id]
    errors.value = next
  }

  function analyze(groupId: string) {
    return groupMutation(groupId, "analyze", () => postJson(analyzeBatchGroupPath(groupId)))
  }

  function rotate(groupId: string, degrees?: number) {
    const rotation = degrees === undefined ? { use_suggestions: true } : { degrees }
    return groupMutation(groupId, "rotate", () =>
      postJson(rotateBatchGroupPath(groupId), { rotation }),
    )
  }

  function createItem(groupId: string, fields: BatchItemFields) {
    return groupMutation(groupId, "create", () =>
      postJson(batchGroupItemPath(groupId), { item: fields }),
    )
  }

  function attach(groupId: string, itemId: string) {
    return groupMutation(groupId, "attach", () =>
      postJson(attachBatchGroupPath(groupId), { attachment: { item_id: itemId } }),
    )
  }

  function skip(groupId: string) {
    return groupMutation(groupId, "skip", () => postJson(skipBatchGroupPath(groupId)))
  }

  function deletePhoto(groupId: string, photoId: string) {
    return groupMutation(groupId, "delete-photo", () =>
      deleteJson(captureGroupPhotoPath(groupId, photoId)),
    )
  }

  function splitGroup(groupId: string, photoIds: string[]) {
    return mutation(
      groupId,
      "split",
      () => postJson(splitBatchGroupPath(groupId), { split: { photo_ids: photoIds } }),
      (groups: CaptureGroup[]) => options.replaceGroups?.(groups),
    )
  }

  function mergeGroups(groupId: string, sourceGroupIds: string[]) {
    return mutation(
      groupId,
      "merge",
      () =>
        postJson(mergeBatchGroupPath(groupId), {
          merge: { source_group_ids: sourceGroupIds },
        }),
      (groups: CaptureGroup[]) => options.replaceGroups?.(groups),
    )
  }

  function archive(batchId: string) {
    return mutation(
      batchId,
      "archive",
      () => patchJson(archiveBatchPath(batchId), {}),
      options.replaceBatch,
    )
  }

  function finish(batchId: string) {
    return mutation(
      batchId,
      "finish",
      () => patchJson(finishBatchPath(batchId), {}),
      options.replaceBatch,
    )
  }

  function updateContainer(batchId: string, containerId: string | null) {
    return mutation(
      batchId,
      "container",
      () =>
        patchJson(batchContainerPath(batchId), {
          container: { container_id: containerId },
        }),
      options.replaceBatch,
    )
  }

  async function deleteBatch(batchId: string): Promise<boolean> {
    if (actionFor(batchId)) return false

    pendingActions.value = { ...pendingActions.value, [batchId]: "delete" }
    clearError(batchId)
    try {
      const response = await deleteJson(batchPath(batchId))
      if (!response.ok) await responseData<never>(response)
      return true
    } catch (error) {
      errors.value = {
        ...errors.value,
        [batchId]: error instanceof Error ? error.message : "The request failed",
      }
      return false
    } finally {
      const next = { ...pendingActions.value }
      delete next[batchId]
      pendingActions.value = next
    }
  }

  return {
    actionFor,
    errorFor,
    analyze,
    rotate,
    createItem,
    attach,
    skip,
    deletePhoto,
    splitGroup,
    mergeGroups,
    finish,
    archive,
    updateContainer,
    deleteBatch,
  }
}

async function responseData<T>(response: Response): Promise<T> {
  const body = (await response.json().catch(() => null)) as DataEnvelope<T> | ErrorEnvelope | null
  if (!response.ok) {
    const error = body as ErrorEnvelope | null
    throw new Error(error?.error?.message ?? `Batch request failed (${response.status})`)
  }
  return (body as DataEnvelope<T>).data
}
